import {
  cloneElement,
  useId,
  useState,
  type ReactElement,
  type ReactNode
} from "react";

import { mergeClassNames } from "./classNames";
import "./controls.css";

interface TooltipTriggerProps {
  "aria-describedby"?: string;
}

export interface TooltipProps {
  children: ReactElement<TooltipTriggerProps>;
  content: ReactNode;
  placement?: "top" | "right" | "bottom" | "left";
  className?: string;
}

export function Tooltip({
  children,
  content,
  placement = "top",
  className
}: TooltipProps) {
  const tooltipId = useId();
  const [isVisible, setIsVisible] = useState(false);
  const existingDescription = children.props["aria-describedby"];
  const describedBy = existingDescription
    ? existingDescription + " " + tooltipId
    : tooltipId;

  return (
    <span
      className={mergeClassNames("ui-tooltip", className)}
      onBlurCapture={(event) => {
        const nextTarget = event.relatedTarget;

        if (
          !(nextTarget instanceof Node) ||
          !event.currentTarget.contains(nextTarget)
        ) {
          setIsVisible(false);
        }
      }}
      onFocusCapture={() => setIsVisible(true)}
      onKeyDownCapture={(event) => {
        if (event.key === "Escape" && isVisible) {
          event.stopPropagation();
          setIsVisible(false);
        }
      }}
      onMouseEnter={() => setIsVisible(true)}
      onMouseLeave={() => setIsVisible(false)}
    >
      {cloneElement(children, { "aria-describedby": describedBy })}
      <span
        className="ui-tooltip__content"
        data-placement={placement}
        data-visible={isVisible}
        id={tooltipId}
        role="tooltip"
      >
        {content}
      </span>
    </span>
  );
}
